import { useContext, useEffect, useState } from 'react';
import AppContext from '../contexts/AppContext';
import { xfdfApi, XfdfModel } from '../utils/api/xfdf';
import tenantApi from '../utils/api/tenant';

type SaveAnnotationModel = Omit<
  XfdfModel,
  'uploadId' | 'createdByUserId' | 'id'
> & {
  isModificationLog?: boolean;
  action?: string;
};

export type AnnotationApi = {
  authorName: string;
  authorRole: string;
  data: XfdfModel[];
  isAppendOnly: boolean;
  loading: boolean;
  index(): Promise<XfdfModel[]>;
  save(model: SaveAnnotationModel): Promise<void>;
};

/**
 * Provides api and manages state for XFDF annotations
 */
export function useAnnotationApi(
  uploadId: number,
  tenantId?: number,
  projectId?: number,
): AnnotationApi {
  const authorName = useAnnotationAuthorName();
  const authorRole = useAnnotationAuthorRole();
  const [data, setData] = useState<XfdfModel[] | null>(null);
  const [isAppendOnly, setIsAppendOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const index = async () => {
    const nextData = await xfdfApi.index(uploadId, projectId);

    setData(nextData);

    return nextData;
  };

  const setAppendOnly = async () => {
    const value = await tenantApi.fetchIsAppendOnly(tenantId!, projectId);
    setIsAppendOnly(value);
  }

  const save = async (model: SaveAnnotationModel) => {
    await xfdfApi.save({ ...model, uploadId });
  };

  const api: AnnotationApi = {
    authorName,
    authorRole,
    data: data || [],
    isAppendOnly,
    loading,
    index,
    save,
  };

  useEffect(() => {
    index();
    if (tenantId) {
      setAppendOnly();
    }
    xfdfApi.initSocket((err: any) => {
      throw err;
    });

    return () => {
      xfdfApi.stopSocket();
    };
  }, []);

  useEffect(() => {
    if (loading && data) {
      setLoading(false);
    }
  }, [loading, data]);

  return api;
}

export function useAnnotationAuthorName() {
  const appStore = useContext(AppContext);

  const user = appStore.get('user');

  if (user && user.email) {
    return user.email;
  }

  return 'Guest';
}

export function useAnnotationAuthorRole() {
  const appStore = useContext(AppContext);

  const user = appStore.get('user');

  if (user && user.userTenantRoles && user.userTenantRoles.length > 0) {
    return user.userTenantRoles[0].role.name;
  }

  return 'Guest';
}
